import {map, pipe, split} from 'ramda'
import {v4 as uuid} from 'uuid'

import {
  Room,
  RoomConfigurationType,
  RoomSplitType,
  UniqueRoomSplitType
} from 'room/types/RoomConfiguration'

const DEFAULT_ROOMS = '2'
const CHILD_SEPARATOR = ','
const GUEST_SEPARATOR = ':'
const ROOM_SEPARATOR = '|'

const roomToRoomSplit = (room: string): RoomSplitType => {
  const [adults, children] = room.split(GUEST_SEPARATOR)

  return {
    adults: Number(adults),
    children: children
      ? pipe(split(CHILD_SEPARATOR), map(Number))(children)
      : []
  }
}

export const roomsSplitToConfigurationObject = (
  roomsSplit: RoomSplitType[]
): RoomConfigurationType => {
  const numberOfAdults = roomsSplit.reduce((acc, room) => acc + room.adults, 0)

  const numberOfChildren = roomsSplit.reduce(
    (acc, room) => acc + (room.children ? room.children.length : 0),
    0
  )

  return {
    numberOfAdults,
    numberOfChildren,
    numberOfRooms: roomsSplit.length,
    roomsSplit
  }
}

export const roomsToConfigurationObject = (
  rooms: string
): RoomConfigurationType => {
  const roomConfig = pipe(
    split(ROOM_SEPARATOR),
    map(roomToRoomSplit)
  )(rooms || DEFAULT_ROOMS)

  return roomsSplitToConfigurationObject(roomConfig)
}

const roomSplitToString = (roomSplit: RoomSplitType): string =>
  roomSplit.children.length > 0
    ? `${roomSplit.adults}:` + roomSplit.children.join(CHILD_SEPARATOR)
    : roomSplit.adults.toString()

export const roomsSplitToString = (
  roomsSplit: readonly RoomSplitType[]
): string => roomsSplit.map(roomSplitToString).join(ROOM_SEPARATOR)

export const numberOfRooms = (rooms: string): number =>
  roomsToConfigurationObject(rooms).numberOfRooms

export const addIdsToRoomsSplit = (
  roomsSplit: RoomSplitType[] = [],
  generateId: () => string = uuid
): UniqueRoomSplitType[] =>
  roomsSplit.map(
    (roomSplit: RoomSplitType): UniqueRoomSplitType => ({
      ...roomSplit,
      id: generateId()
    })
  )

export const removeIdsFromRoomsSplit = (
  roomsSplit: UniqueRoomSplitType[]
): RoomSplitType[] =>
  roomsSplit.map(
    ({adults, children}: UniqueRoomSplitType): RoomSplitType => ({
      adults,
      children
    })
  )

export const totalNumberOfGuestsPerRoom = (roomSplit: RoomSplitType) => {
  return roomSplit.adults + roomSplit.children.length
}

export const roomsToRoomsSplit = (rooms: Room[]) =>
  rooms.map(room => ({
    adults: room.guests.adults,
    children: room.guests.childAges
  }))

export const roomsToString = (rooms: Room[]): string => {
  const roomsSplit = roomsToRoomsSplit(rooms)
  return roomsSplitToString(roomsSplit)
}
